import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ClientSpecialAgreement } from '../types/models/SpecialDeal';
import type { ApiResponseClientSpecials } from '../types/api/ApiResponseSpecialDeal';

interface UseClientSpecialsReturn {
  specials: ClientSpecialAgreement[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /clients/specials — the in-scope organization's ACTIVE special agreements (tenant read).
// useApiFetch injects the impersonation header, so the list follows the impersonated client with no
// extra work here. Consumers split the rows: `parent_type === null` are STANDALONE agreements (the
// Agreements page); `parent_type === 'virtual_machine'` rows render on their parent VM's overview.
//
// `enabled` (default true) defers the fetch — e.g. a page whose staff variant reads the richer
// admin list instead passes `!isStaff` so only one of the two lists is ever requested.
export const useClientSpecials = (enabled = true): UseClientSpecialsReturn => {
  const [specials, setSpecials] = useState<ClientSpecialAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSpecials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/specials');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseClientSpecials | null;
      if (body?.status === 'success' && body.data?.specials) {
        setSpecials(body.data.specials);
      } else {
        setError(body?.message ?? 'Failed to load special agreements');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching special agreements');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchSpecials();
    }
  }, [enabled, auth.isAuthenticated, fetchSpecials]);

  return { specials, loading, error, refetch: fetchSpecials };
};
